import React, { useEffect, useState } from "react";
import api from "../api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../components/ui/sheet";

const STATUS_COLOR = {
  done: "text-emerald-400 border-emerald-500/30",
  failed: "text-red-400 border-red-500/30",
  canceled: "text-muted-foreground border-border",
  expired: "text-muted-foreground border-border",
  downloading: "text-amber-400 border-amber-500/30",
  cutting: "text-amber-400 border-amber-500/30",
  uploading_s3: "text-amber-400 border-amber-500/30",
  sending_telegram: "text-amber-400 border-amber-500/30",
  queued: "text-blue-400 border-blue-500/30",
  probing: "text-blue-400 border-blue-500/30",
  received: "text-foreground border-border",
  awaiting_cut_params: "text-foreground border-border",
};

const FILTERS = ["all", "queued", "downloading", "done", "failed", "canceled"];

export default function Jobs() {
  const [filter, setFilter] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (f) => {
    setLoading(true);
    const params = f && f !== "all" ? { status_: f } : {};
    const { data } = await api.get("/admin/jobs", { params });
    setJobs(data);
    setLoading(false);
  };

  useEffect(() => {
    load(filter);
    const id = setInterval(() => load(filter), 8_000);
    return () => clearInterval(id);
  }, [filter]);

  const onOpen = async (id) => {
    setOpenId(id);
    const { data } = await api.get(`/admin/jobs/${id}`);
    setOpenJob(data);
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Jobs</h1>
        <div className="text-xs font-mono text-muted-foreground">
          {loading ? "…" : `${jobs.length} rows`}
        </div>
      </div>

      <div className="flex gap-1 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`jobs-filter-${f}`}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] border ${
              filter === f
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="border border-border" data-testid="jobs-table">
        {/* ── Desktop (≥lg): grid-таблица ── */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="min-w-[720px]">
        <div className="grid grid-cols-[1fr_140px_120px_180px_120px] gap-4 px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground border-b border-border bg-secondary/20">
          <div>URL</div>
          <div>Status</div>
          <div>Type</div>
          <div>Created</div>
          <div className="text-right">Size</div>
        </div>
        {jobs.length === 0 && !loading && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            No jobs match this filter.
          </div>
        )}
        {jobs.map((j) => (
          <button
            key={j.id}
            onClick={() => onOpen(j.id)}
            data-testid={`job-row-${j.id}`}
            className="w-full grid grid-cols-[1fr_140px_120px_180px_120px] gap-4 px-4 py-2.5 text-sm border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors text-left"
          >
            <div className="truncate font-mono text-xs text-muted-foreground">
              {j.source_url}
            </div>
            <div>
              <span
                className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${
                  STATUS_COLOR[j.status] || "border-border"
                }`}
              >
                {j.status}
              </span>
            </div>
            <div className="font-mono text-xs">{j.job_type}</div>
            <div className="font-mono text-xs text-muted-foreground">
              {new Date(j.created_at).toLocaleString()}
            </div>
            <div className="font-mono text-xs text-right">
              {j.output_size_bytes
                ? `${(j.output_size_bytes / 1024 / 1024).toFixed(1)} MB`
                : "—"}
            </div>
          </button>
        ))}
          </div>
        </div>

        {/* ── Mobile (<lg): card layout ── */}
        <div className="lg:hidden divide-y divide-border">
          {jobs.length === 0 && !loading && (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              No jobs match this filter.
            </div>
          )}
          {jobs.map((j) => (
            <button
              key={j.id}
              onClick={() => onOpen(j.id)}
              data-testid={`job-row-${j.id}`}
              className="w-full px-4 py-3 text-sm text-left hover:bg-secondary/30 transition-colors space-y-2"
            >
              <div className="flex items-start justify-between gap-2 min-w-0">
                <div className="font-mono text-xs text-muted-foreground truncate flex-1">
                  {j.source_url}
                </div>
                <span
                  className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border shrink-0 ${
                    STATUS_COLOR[j.status] || "border-border"
                  }`}
                >
                  {j.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs font-mono text-muted-foreground gap-2 flex-wrap">
                <span>{j.job_type}</span>
                <span>{new Date(j.created_at).toLocaleString()}</span>
                <span>
                  {j.output_size_bytes
                    ? `${(j.output_size_bytes / 1024 / 1024).toFixed(1)} MB`
                    : "—"}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent
          side="right"
          className="bg-background border-l border-border w-full sm:max-w-lg overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle className="font-display text-xl">
              {openJob?.id?.slice(0, 8)}…
            </SheetTitle>
            <SheetDescription className="text-xs font-mono text-muted-foreground">
              Full lifecycle, error and delivery details for this job.
            </SheetDescription>
          </SheetHeader>
          {openJob && (
            <div className="mt-6 space-y-4 text-sm">
              <Field label="URL" value={openJob.source_url} mono />
              <Field label="Status" value={openJob.status} />
              <Field label="Type" value={openJob.job_type} />
              <Field
                label="Created"
                value={new Date(openJob.created_at).toLocaleString()}
              />
              <Field
                label="Started"
                value={
                  openJob.started_at
                    ? new Date(openJob.started_at).toLocaleString()
                    : "—"
                }
              />
              <Field
                label="Finished"
                value={
                  openJob.finished_at
                    ? new Date(openJob.finished_at).toLocaleString()
                    : "—"
                }
              />
              <Field
                label="Size"
                value={
                  openJob.output_size_bytes
                    ? `${(openJob.output_size_bytes / 1024 / 1024).toFixed(2)} MB`
                    : "—"
                }
              />
              <Field label="Delivery" value={openJob.delivery_type || "—"} />
              {openJob.error_code && (
                <Field label="Error code" value={openJob.error_code} tone="danger" />
              )}
              {openJob.error_message && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    Error message
                  </div>
                  <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-secondary/30 p-3 border border-border max-h-64 overflow-auto">
                    {openJob.error_message}
                  </pre>
                </div>
              )}
              <Field label="Trace" value={openJob.trace_id} mono />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, value, mono, tone }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-1 ${mono ? "font-mono text-xs break-all" : "text-sm"} ${
          tone === "danger" ? "text-red-400" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}
